/**
 * «Что вошло в канон» — раскрытие раздела «Что здесь настоящее».
 *
 * Раньше это был отдельный экран `/registry` с четырьмя крупными плашками
 * («Всего земель», «Пашня», «Контуры», «Хозяйства»), дословно повторявшими
 * четыре плашки района. Два экрана показывали одни и те же числа под разными
 * заголовками, и вопрос «чем они отличаются» человек задавал каждый раз
 * заново. Правило уровня отвечает на него однозначно: районные величины живут
 * на районе, а строки источника и замечания качества — здесь (`lib/levels.ts`,
 * отказ L1). Поэтому плашек больше нет, а осталось то, чего нет больше нигде:
 * **из чего это собрано**.
 *
 * Таблица отвечает на один вопрос — «что источник дал и чем это стало»: строки
 * файла с одной стороны, записи канона с другой. Гектаров в ней нет намеренно:
 * сверка площадей стоит выше, в первом взгляде раздела, и второй её показ
 * означал бы два места, где одно и то же равенство может разойтись.
 *
 * Второй таблицы 22 массивов здесь тоже больше нет. Она вела по имени массива
 * в РЕЕСТР КОНТУРОВ, то есть на два уровня вниз мимо самого массива, и была
 * третьей копией сравнения территорий. Вместо неё — переход туда, где
 * территории сравниваются по объявленной оси.
 */

import { Link } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import type { RegistrySummary } from "../../api/client";
import { Button, Count, InlineAlert } from "../../components/ui";
import { formatCount } from "../../lib/format";
import { levelPath } from "../../lib/levels";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import styles from "../features.module.css";
import { areaText } from "./areaText";

const m = defineMessages({
  caption: {
    id: "registry.summary.caption",
    defaultMessage:
      "Что источник дал и чем это стало в каноне. Строка источника — это запись файла, а не участок земли: одна и та же земля может прийти несколькими строками, и в канон она входит один раз.",
    description: "Подпись над таблицей раздела «Данные» → «Что вошло в канон» (хоким, менеджер); объясняет, чем строка источника отличается от участка земли",
  },
  colWhat: { id: "registry.summary.col.what", defaultMessage: "Что", description: "Заголовок первой колонки таблицы состава канона: вид записи; узкая колонка" },
  colCount: { id: "registry.summary.col.count", defaultMessage: "Записей", description: "Заголовок числовой колонки таблицы состава канона: сколько записей; узкая колонка" },
  colMeaning: { id: "registry.summary.col.meaning", defaultMessage: "Чем является", description: "Заголовок колонки таблицы состава канона: пояснение, чем является этот вид записей" },
  sourceRowsName: { id: "registry.summary.sourceRows.name", defaultMessage: "Строки источника", description: "Название строки таблицы состава канона: записи принятого файла выгрузки" },
  sourceRowsMeaning: {
    id: "registry.summary.sourceRows.meaning",
    defaultMessage: "записи принятого файла — то, из чего собран весь остальной список",
    description: "Пояснение к строке «Строки источника» в таблице состава канона; пишется строчными",
  },
  contoursName: { id: "registry.summary.contours.name", defaultMessage: "Контуры", description: "Название строки таблицы состава канона: контуры (участки земли по кадастру)" },
  contoursMeaning: {
    id: "registry.summary.contours.meaning",
    defaultMessage: "единицы земли канона; строка без связи с субъектом контуром не становится",
    description: "Пояснение к строке «Контуры» в таблице состава канона; пишется строчными; субъект — хозяйство или иной владелец земли",
  },
  partsName: { id: "registry.summary.parts.name", defaultMessage: "Части контуров", description: "Название строки таблицы состава канона: части контуров" },
  partsMeaning: {
    id: "registry.summary.parts.meaning",
    defaultMessage: "доли одного контура у разных землепользователей; со своим контуром не складываются",
    description: "Пояснение к строке «Части контуров» в таблице состава канона; пишется строчными",
  },
  landUsesName: { id: "registry.summary.landUses.name", defaultMessage: "Землепользования", description: "Название строки таблицы состава канона: записи о том, кто пользуется землёй" },
  landUsesMeaning: {
    id: "registry.summary.landUses.meaning",
    defaultMessage: "связь «кто пользуется этой землёй и на каком основании»",
    description: "Пояснение к строке «Землепользования» в таблице состава канона; пишется строчными",
  },
  areaRowsName: { id: "registry.summary.areaRows.name", defaultMessage: "Строки площади", description: "Название строки таблицы состава канона: записи площадей по категориям земли" },
  areaRowsMeaning: {
    id: "registry.summary.areaRows.meaning",
    defaultMessage: "площади по категориям; пустая площадь остаётся «нет данных», а не нулём",
    description: "Пояснение к строке «Строки площади» в таблице состава канона; пишется строчными",
  },
  placementsName: { id: "registry.summary.placements.name", defaultMessage: "Размещения", description: "Название строки таблицы состава канона: размещения культур (сколько гектаров какой культуры у хозяйства)" },
  placementsMeaning: {
    id: "registry.summary.placements.meaning",
    defaultMessage: "официальные суммы гектаров по культурам у хозяйства; контуры источником не названы",
    description: "Пояснение к строке «Размещения» в таблице состава канона; пишется строчными",
  },
  actorsName: { id: "registry.summary.actors.name", defaultMessage: "Субъекты", description: "Название строки таблицы состава канона: субъекты (хозяйства и прочие владельцы/пользователи земли)" },
  actorsMeaning: {
    id: "registry.summary.actors.meaning",
    defaultMessage: "из них хозяйств: {farms} — остальные не фермерские",
    description: "Пояснение к строке «Субъекты» в таблице состава канона; {farms} — число фермерских хозяйств среди субъектов",
  },
  massifsName: { id: "registry.summary.massifs.name", defaultMessage: "Массивы", description: "Название строки таблицы состава канона: массивы (территории района)" },
  massifsMeaning: {
    id: "registry.summary.massifs.meaning",
    defaultMessage: "территории района — уровень между районом и хозяйством",
    description: "Пояснение к строке «Массивы» в таблице состава канона; пишется строчными",
  },
  excludedTitle: {
    id: "registry.summary.excluded.title",
    defaultMessage: "Что НЕ вошло — объявлено построчно",
    description: "Заголовок предупреждающего блока под таблицей состава канона: что исключено из канона; «НЕ» прописными намеренно",
  },
  excludedBody: {
    id: "registry.summary.excluded.body",
    defaultMessage:
      "Строк источника не связано ни с одним субъектом: <b>{notLinked}</b> — контурами они не называются. Номеров с конфликтом, всё ещё исключённых: <b>{conflictNumbers}</b> ({conflictRows, plural, one {# строка} few {# строки} many {# строк} other {# строки}}, {conflictArea}) — записи не создаются и не перепривязываются.",
    description:
      "Абзац в блоке «Что НЕ вошло»; {notLinked} и {conflictNumbers} — числа, {conflictRows} — число строк источника (склоняется), {conflictArea} — площадь с единицей («12,5 га»). Номер — номер контура в кадастре",
  },
  resolvedBody: {
    id: "registry.summary.resolved.body",
    defaultMessage:
      "Разобрано планом решений: <b>{numbers}</b> {numbersCount, plural, one {номер} few {номера} many {номеров} other {номера}} — {rows, plural, one {# строка} few {# строки} many {# строк} other {# строки}}, {area}. Из них перенесено в канон {createdArea} и отброшено как та же земля {dropped, plural, one {# строка} few {# строки} many {# строк} other {# строки}} ({droppedArea}). Задокументировано решений: <b>{decisions}</b> — каждое с автором, методом и основанием.",
    description:
      "Абзац в блоке «Что НЕ вошло», когда план решений уже применён; {numbers}/{decisions} — числа, {numbersCount} — то же число для склонения слова «номер» (номер контура в кадастре), {rows}/{dropped} — числа строк источника, {area}/{createdArea}/{droppedArea} — площади с единицей",
  },
  compareButton: { id: "registry.summary.compare.button", defaultMessage: "Сравнение территорий", description: "Кнопка-ссылка под таблицей состава канона: переход к сравнению массивов на «Картине района»" },
  compareHint: {
    id: "registry.summary.compare.hint",
    defaultMessage:
      "{count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}} района сравниваются на «Картине района» — по одной объявленной оси, а не по сводной оценке. Имя массива там открывает его уровень.",
    description: "Пояснение рядом с кнопкой «Сравнение территорий»; {count} — число массивов района (склоняется)",
  },
});

export function RegistrySummarySection({ data }: { data: RegistrySummary }) {
  const t = useT();
  // До применения плана решений разобранного нет — тогда абзац не показываем,
  // а не печатаем «разобрано 0».
  const resolvedConflicts = data.excluded.contour_no_conflict_resolved_numbers;
  const context = useAnalysisContext();
  const bold = (chunks: React.ReactNode) => <strong>{chunks}</strong>;
  const cnt = (value: number) => formatCount(value).text;

  return (
    <>
      <div className={styles.scrollX}>
        <table className={styles.plainTable}>
          <caption>{t(m.caption)}</caption>
          <thead>
            <tr>
              <th scope="col">{t(m.colWhat)}</th>
              <th scope="col" className={styles.num}>
                {t(m.colCount)}
              </th>
              <th scope="col">{t(m.colMeaning)}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t(m.sourceRowsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.source_records} />
              </td>
              <td>{t(m.sourceRowsMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.contoursName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.contours} />
              </td>
              <td>{t(m.contoursMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.partsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.contour_parts} />
              </td>
              <td>{t(m.partsMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.landUsesName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.land_uses} />
              </td>
              <td>{t(m.landUsesMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.areaRowsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.contour_areas} />
              </td>
              <td>{t(m.areaRowsMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.placementsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.placements} />
              </td>
              <td>{t(m.placementsMeaning)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.actorsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.actors} />
              </td>
              <td>{t(m.actorsMeaning, { farms: cnt(data.counts.actors_farmer_default) })}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.massifsName)}</th>
              <td className={styles.num}>
                <Count value={data.counts.massifs} />
              </td>
              <td>{t(m.massifsMeaning)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InlineAlert title={t(m.excludedTitle)}>
        <p>
          {t(m.excludedBody, {
            b: bold,
            notLinked: cnt(data.excluded.contour_rows_not_linked),
            conflictNumbers: cnt(data.excluded.contour_no_conflict_numbers),
            conflictRows: data.excluded.contour_no_conflict_rows,
            conflictArea: areaText(data.excluded.contour_no_conflict_microha),
          })}
        </p>
        {resolvedConflicts > 0 ? (
          <p>
            {t(m.resolvedBody, {
              b: bold,
              numbers: cnt(data.excluded.contour_no_conflict_resolved_numbers),
              numbersCount: data.excluded.contour_no_conflict_resolved_numbers,
              rows: data.excluded.contour_no_conflict_resolved_rows,
              area: areaText(data.excluded.contour_no_conflict_resolved_microha),
              createdArea: areaText(data.excluded.contour_no_conflict_created_microha),
              dropped: data.excluded.contour_no_conflict_dropped_rows,
              droppedArea: areaText(data.excluded.contour_no_conflict_dropped_microha),
              decisions: cnt(data.excluded.contour_no_conflict_decisions),
            })}
          </p>
        ) : null}
      </InlineAlert>

      {/* Территории сравниваются в одном месте и по объявленной оси. Отсюда —
          переход, а не третья копия той же таблицы. */}
      <div className={styles.rowWrap}>
        <Link to={{ pathname: levelPath("district"), search: context.linkSearch() }}>
          <Button>{t(m.compareButton)}</Button>
        </Link>
        <span>{t(m.compareHint, { count: data.counts.massifs })}</span>
      </div>
    </>
  );
}
